// Round results (ui-qol §7): a white 325 × 300 card in the centre column, "Round results", six
// stats placed in two columns (caption 15 px #000, value 12 px bold #444), the mass-over-time
// graph from DEATH.samples, a 1 px rule and a 306 × 34 blue Continue at the bottom.
//
// Timing (ui-qol §3): the card is mounted 0.36 s after the death with opacity 0 and is already
// clickable; it fades in from +2.95 s to +3.45 s (the canvas dim ramps 0 → 0.5 meanwhile).
// There is no Continue guard; Esc continues (app.ts), Enter does nothing (parity).
//
// Money rooms (money-mode §5, §7): a red "Value lost: $x" line under the title.
//
// Consumer: app.ts (`summary` state); mounted in #menu where it replaces the play card. The
// cash-out screen (ui/modals/cashout.ts) reuses the stats block, the graph and the chrome.

import { type DeathMessage, type LifeSummary, type Rgb, rgbToCss } from '@ballio/shared';
import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';

export interface SummaryDeps {
  /** Continue (button or Esc): go to home with the nick field focused. */
  onContinue(): void;
  /** Practice rooms: "Play for real · $1" (leave practice for the money door). */
  onPlayForReal?(): void;
  /** The click sound hook (audio §3 row 7). */
  playClick?(): void;
}

export interface SummaryExtra {
  /** Money rooms: the formatted value lost ("$0.90"), or null/undefined in free rooms. */
  readonly valueLostText?: string | null;
  /** Practice rooms (money-mode §1.4): the "Play for real" button next to "Practice again". */
  readonly practice?: PracticeResults | null;
}

/** The practice results buttons (money-mode §1.4): Continue reads "Practice again". */
export interface PracticeResults {
  /** "Play for real · $1", or "Real money · coming soon" while money is off. */
  readonly realText: string;
  /** False while the server has no money arenas (the button shows, disabled). */
  readonly realEnabled: boolean;
}

/**
 * The second results button of a practice life ("Play for real · $1"), hidden otherwise, and
 * `apply(practice)` relabels Continue and toggles `has-real` on the card. Shared by the death and
 * cash-out screens.
 */
export function practiceResultsButtons(
  card: HTMLElement,
  continueBtn: HTMLElement,
  isMounted: () => boolean,
  onPlayForReal: (() => void) | undefined,
  playClick: (() => void) | undefined,
): { readonly el: HTMLElement; apply(practice: PracticeResults | null): void } {
  const el = h(
    'button',
    {
      type: 'button',
      class: 'btn-results btn-results-real',
      hidden: true,
      onClick: () => {
        if (!isMounted() || (el as HTMLButtonElement).disabled) return;
        playClick?.();
        onPlayForReal?.();
      },
    },
    '',
  );
  return {
    el,
    apply(practice: PracticeResults | null): void {
      const on = practice !== null && onPlayForReal !== undefined;
      continueBtn.textContent = on ? t('practice.again') : t('summary.continue');
      el.textContent = on ? practice.realText : '';
      (el as HTMLButtonElement).disabled = !on || !practice.realEnabled;
      show(el, on);
      card.classList.toggle('has-real', on);
    },
  };
}

export interface SummaryScreen {
  readonly el: HTMLElement;
  readonly visible: boolean;
  /** `color`: the life's cell colour for the graph. */
  show(death: LifeSummary, nowMs: number, color?: Rgb, extra?: SummaryExtra): void;
  hide(): void;
  /** Esc while shown: runs onContinue once the card is mounted; true if it did. */
  tryContinue(nowMs: number): boolean;
}

/** ui-qol §3: the card is mounted (clickable, still transparent) this long after the death. */
export const RESULTS_MOUNT_MS = 360;
/** ui-qol §3: the fade-in starts this long after the death … */
export const RESULTS_FADE_START_MS = 2950;
/** … and lasts this long (0 with Reduce motion). */
export const RESULTS_FADE_MS = 500;

/** `m:ss` (ui-qol §7). Minutes keep counting past 59 (`75:03`); bad input reads `0:00`. */
export function formatDuration(totalSec: number): string {
  const s = Number.isFinite(totalSec) && totalSec > 0 ? Math.floor(totalSec) : 0;
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss < 10 ? '0' : ''}${ss}`;
}

/** A whole number with thousands separators (stats are integers on the wire). */
export function formatCount(n: number): string {
  const v = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  return v.toLocaleString('en-US');
}

export type SummaryStatKey =
  'pellets' | 'topMass' | 'timeAlive' | 'boardTime' | 'ballsEaten' | 'bestRank';

export interface SummaryStat {
  readonly key: SummaryStatKey;
  /** Lower-case caption (ui-qol §13 `summary.*`). */
  readonly caption: string;
  /** Formatted value. */
  readonly value: string;
}

/**
 * The six stats in reading order (ui-qol §7): L1 pellets eaten, R1 top mass, L2 time alive,
 * R2 time on the board, L3 cells eaten, R3 best rank (`n`, or `—` when never ranked).
 */
export function summaryStats(d: LifeSummary): SummaryStat[] {
  return [
    { key: 'pellets', caption: t('summary.pellets'), value: formatCount(d.pelletsEaten) },
    { key: 'topMass', caption: t('summary.topMass'), value: formatCount(d.highestMass) },
    { key: 'timeAlive', caption: t('summary.timeAlive'), value: formatDuration(d.timeAliveSec) },
    { key: 'boardTime', caption: t('summary.boardTime'), value: formatDuration(d.boardTimeSec) },
    { key: 'ballsEaten', caption: t('summary.ballsEaten'), value: formatCount(d.ballsEaten) },
    {
      key: 'bestRank',
      caption: t('summary.bestRank'),
      value: d.bestRank > 0 ? String(Math.floor(d.bestRank)) : t('summary.noRank'),
    },
  ];
}

/** True once the card is mounted (`RESULTS_MOUNT_MS` after the death); Continue works from then. */
export function continueReady(shownAtMs: number, nowMs: number): boolean {
  return nowMs - shownAtMs >= RESULTS_MOUNT_MS;
}

/** Graph canvas size (ui-qol §7: full inner width × 45 px). */
export const GRAPH_W = 306;
export const GRAPH_H = 45;

/**
 * The mass graph's polyline in canvas pixels (y down, 0 → max sample), or null when only the
 * baseline is drawn. ui-qol §7 edge-case rule (until R3-6 pins agar's): draw the line only with
 * ≥ 2 samples and a max of at least twice the first sample.
 */
export function massGraphPoints(
  samples: readonly number[],
  w: number = GRAPH_W,
  hgt: number = GRAPH_H,
): Array<readonly [number, number]> | null {
  const clean = samples.filter((v) => Number.isFinite(v) && v >= 0);
  if (clean.length < 2) return null;
  const first = clean[0] as number;
  const max = Math.max(...clean);
  if (!(max > 0) || max < 2 * first) return null;
  const pad = 1.5; // half the 3 px line, so it is not clipped at the edges
  const step = (w - 2 * pad) / (clean.length - 1);
  return clean.map((v, i) => [pad + i * step, hgt - pad - (v / max) * (hgt - 2 * pad)] as const);
}

/** Draws the graph (3 px round polyline, area at α 0.5, 1 px #CCC baseline) into `canvas`. */
export function drawMassGraph(
  canvas: HTMLCanvasElement,
  samples: readonly number[],
  color: Rgb,
): void {
  const dpr = Math.max(1, Math.min(3, globalThis.devicePixelRatio || 1));
  canvas.width = Math.round(GRAPH_W * dpr);
  canvas.height = Math.round(GRAPH_H * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, GRAPH_W, GRAPH_H);
  ctx.fillStyle = '#CCCCCC';
  ctx.fillRect(0, GRAPH_H - 1, GRAPH_W, 1);
  const pts = massGraphPoints(samples);
  if (!pts) return;
  const css = rgbToCss(color);
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  const lastX = (pts[pts.length - 1] as readonly [number, number])[0];
  const firstX = (pts[0] as readonly [number, number])[0];
  ctx.lineTo(lastX, GRAPH_H);
  ctx.lineTo(firstX, GRAPH_H);
  ctx.closePath();
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = css;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  ctx.lineWidth = 3;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = css;
  ctx.stroke();
}

/** The six stat cells (shared with the cash-out screen). */
export function statsBlock(d: LifeSummary): HTMLElement {
  return h(
    'dl',
    { class: 'results-stats' },
    summaryStats(d).map((s, i) =>
      h(
        'div',
        { class: `results-stat stat-${s.key} ${i % 2 === 0 ? 'is-left' : 'is-right'}` },
        h('dt', { class: 'results-caption' }, s.caption),
        h('dd', { class: 'results-value' }, s.value),
      ),
    ),
  );
}

/** Fallback graph colour when the life colour is unknown. */
const DEFAULT_GRAPH_COLOR: Rgb = 0x54c800;

/**
 * The results reveal (ui-qol §3) for a card: mounted `RESULTS_MOUNT_MS` after `shownAtMs`, then
 * the CSS fade (`.results-card.is-revealing`, delay = fade start − mount). Returns a canceller.
 */
export function revealResults(el: HTMLElement, onMount: () => void): () => void {
  el.classList.remove('is-revealing');
  el.style.setProperty('--reveal-delay', `${RESULTS_FADE_START_MS - RESULTS_MOUNT_MS}ms`);
  el.style.setProperty('--reveal-ms', `${RESULTS_FADE_MS}ms`);
  const timer = setTimeout(() => {
    show(el, true);
    // Restart the animation on every show.
    void el.offsetWidth;
    el.classList.add('is-revealing');
    onMount();
  }, RESULTS_MOUNT_MS);
  return () => clearTimeout(timer);
}

export function createSummary(deps: SummaryDeps): SummaryScreen {
  let visible = false;
  let mounted = false;
  let shownAt = Number.NEGATIVE_INFINITY;
  let cancelReveal: (() => void) | null = null;

  const lost = h('p', { class: 'results-lost', hidden: true });
  const statsWrap = h('div', { class: 'results-stats-wrap' });
  const graph = h('canvas', {
    class: 'results-graph',
    width: GRAPH_W,
    height: GRAPH_H,
    role: 'img',
    'aria-label': t('summary.graph'),
  });
  const continueBtn = h(
    'button',
    {
      type: 'button',
      class: 'btn-results',
      onClick: () => {
        if (!mounted) return;
        deps.playClick?.();
        deps.onContinue();
      },
    },
    t('summary.continue'),
  );
  const el = h(
    'section',
    { class: 'card results-card', 'aria-labelledby': 'summary-title', hidden: true },
    h('h2', { class: 'results-title', id: 'summary-title' }, t('summary.title')),
    lost,
    statsWrap,
    h('hr', { class: 'results-rule' }),
    graph,
    continueBtn,
  );
  const real = practiceResultsButtons(
    el,
    continueBtn,
    () => mounted,
    deps.onPlayForReal,
    deps.playClick,
  );
  el.append(real.el);

  const screen: SummaryScreen = {
    el,
    get visible() {
      return visible;
    },
    show(death: LifeSummary | DeathMessage, nowMs: number, color?: Rgb, extra?: SummaryExtra) {
      statsWrap.replaceChildren(statsBlock(death));
      const lostText = extra?.valueLostText ?? null;
      lost.textContent = lostText === null ? '' : t('money.valueLost', { v: lostText });
      show(lost, lostText !== null);
      el.classList.toggle('has-money', lostText !== null);
      real.apply(extra?.practice ?? null);
      drawMassGraph(graph, death.samples, color ?? DEFAULT_GRAPH_COLOR);
      visible = true;
      mounted = false;
      shownAt = nowMs;
      show(el, false);
      cancelReveal?.();
      cancelReveal = revealResults(el, () => {
        mounted = true;
        // ui-qol §7: Enter does nothing here (agar leaves focus on the page), so no button
        // keeps focus; Esc continues (app.ts).
        const active = el.ownerDocument.activeElement;
        if (active instanceof HTMLElement && active !== el.ownerDocument.body) active.blur();
      });
    },
    hide(): void {
      visible = false;
      mounted = false;
      cancelReveal?.();
      cancelReveal = null;
      el.classList.remove('is-revealing');
      show(el, false);
    },
    tryContinue(nowMs: number): boolean {
      if (!visible || !(mounted || continueReady(shownAt, nowMs))) return false;
      deps.onContinue();
      return true;
    },
  };
  return screen;
}
